<template>
  <v-dialog v-model="open" max-width="1300" @keydown.esc="$emit('updateDialog', false)">
    <v-card>
      <v-form @submit.prevent="createItem">

        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text class="pt-5 pb-2">

          <!-- Precarga desde plantilla -->
          <v-card v-if="!isViewModal() && item.id === '' && revertOf === null" outlined class="mb-5 template-card">
            <div class="pa-4">
              <div class="d-flex align-center mb-1">
                <v-icon color="#1565C0" class="mr-2" size="20">mdi-content-copy</v-icon>
                <span class="subtitle-1 font-weight-bold grey--text text--darken-3">Precargar desde plantilla</span>
              </div>
              <div class="caption grey--text text--darken-1 mb-4">
                Las plantillas son movimientos que se repiten seguido y ya vienen armados. Al elegir una se completan el tipo de
                comprobante, el emisor, el concepto y las líneas contables (cuentas, centro de costo y concepto). Solo resta cargar
                los importes de Debe y Haber. Podés modificar cualquier dato antes de guardar.
              </div>
              <v-row>
                <v-col cols="12" md="6" class="pt-2 pb-3">
                  <v-select
                    v-model="selected_template"
                    :items="templates"
                    item-text="name"
                    return-object
                    label="Plantilla"
                    no-data-text="No hay plantillas creadas"
                    hide-details
                    dense
                    clearable
                    @change="applyTemplate"
                  ></v-select>
                </v-col>
              </v-row>
            </div>
          </v-card>

          <!-- Aviso de reversión: la fecha define en qué período impacta la vuelta -->
          <v-alert v-if="revertOf !== null" dense outlined type="warning" class="mb-4 caption">
            La fecha es la del impacto de la reversión, no la del movimiento original. Los reportes de
            tesorería son de lo percibido: la vuelta cae en el período que elijas acá. No puede ser
            anterior al movimiento original ni futura.
          </v-alert>

          <!-- Aviso de edición acotada -->
          <v-alert v-if="isEditModal()" dense outlined type="info" class="mb-4 caption">
            En un movimiento ya registrado solo se pueden corregir el concepto, la referencia y el motivo de cada línea.
            Para cambiar importes, cuentas o fecha hay que revertirlo y cargarlo de nuevo.
          </v-alert>

          <!-- Campos principales -->
          <v-row>
            <v-col cols="12" md="3">
              <DatePickerEsp
                label="Fecha*"
                name="date"
                :date="item.date"
                :isRequired="true"
                :isViewModal="isLockedFields()"
                @setDate="
                  (date) => {
                    item.date = date;
                  }
                "
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-combobox
                v-model="item.clase_comprobante"
                data-vv-as="tipo de comprobante"
                name="clase_comprobante"
                item-text="name"
                item-value="id"
                :items="clases_comprobante"
                label="Tipo de Comprobante*"
                :disabled="isLockedFields()"
                autocomplete="off"
                clearable
                v-validate="'required|combobox_required'"
              ></v-combobox>
              <span class="red--text caption">{{ errors.first("clase_comprobante") }}</span>
            </v-col>

            <v-col cols="12" md="3">
              <v-combobox
                v-model="item.emitter"
                data-vv-as="emisor"
                name="emitter"
                item-text="name"
                item-value="id"
                :items="emitters"
                label="Emisor*"
                :disabled="isLockedFields()"
                autocomplete="off"
                clearable
                v-validate="'required|combobox_required'"
              ></v-combobox>
              <span class="red--text caption">{{ errors.first("emitter") }}</span>
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="item.reference"
                label="Referencia"
                :placeholder="isViewModal() ? '' : 'Ej. REF-00123'"
                autocomplete="off"
                :disabled="isViewModal()"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Concepto -->
          <v-row class="mt-0">
            <v-col cols="12">
              <v-text-field
                v-model="item.concept"
                label="Concepto"
                :placeholder="isViewModal() ? '' : 'Describa el motivo general del movimiento...'"
                autocomplete="off"
                :disabled="isViewModal()"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Detalles Contables -->
          <div class="d-flex justify-space-between align-center mt-3 mb-3">
            <div class="d-flex align-center">
              <v-icon color="#1565C0" class="mr-2" size="20">format_list_bulleted</v-icon>
              <span class="subtitle-1 font-weight-bold grey--text text--darken-3">Detalles Contables</span>
            </div>
            <v-btn v-if="!isLockedFields()" color="primary" small @click="addDetail">
              <v-icon small left>add</v-icon>
              Agregar Item
            </v-btn>
          </div>

          <!-- Tabla de detalles -->
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="detail-th">#</th>
                  <th class="detail-th">Cuenta</th>
                  <th class="detail-th">Centro / Concepto</th>
                  <th class="detail-th text-center">Debe</th>
                  <th class="detail-th text-center">Haber</th>
                  <th class="detail-th">Descripción</th>
                  <th class="detail-th" v-if="!isLockedFields()"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, idx) in item.details" :key="idx">
                  <td class="text-center grey--text text--darken-1" style="min-width:40px; font-size:13px; font-weight:500">
                    {{ String(idx + 1).padStart(2, '0') }}
                  </td>
                  <td style="min-width:190px">
                    <v-combobox
                      v-model="detail.money_account"
                      item-text="name"
                      item-value="id"
                      :items="money_accounts"
                      hide-details
                      :disabled="isLockedFields()"
                      class="py-1"
                    ></v-combobox>
                    <span v-if="submitted && (!detail.money_account || !detail.money_account.id)" class="red--text caption">
                      Seleccione una cuenta.
                    </span>
                  </td>
                  <td style="min-width:160px">
                    <ConceptoCentroPicker
                      v-if="detail.money_account && detail.money_account.is_resultado"
                      :concepto.sync="detail.concepto"
                      :cost_center.sync="detail.cost_center"
                      :conceptos="conceptos"
                      :cost_centers="cost_centers"
                      :disabled="isLockedFields()"
                      :inTable="true"
                    />
                    <span v-else class="grey--text caption">—</span>
                  </td>
                  <td style="min-width:120px" :class="isLockedFields() ? 'text-right' : ''">
                    <span v-if="isLockedFields()">{{ parseFloat(detail.amount_debe) > 0 ? formatUtils.formatMoney(detail.amount_debe) : "—" }}</span>
                    <template v-else>
                      <v-text-field
                        v-model="detail.amount_debe"
                        hide-details
                        :disabled="!!detail.amount_haber"
                        class="py-1 input-right"
                        @input="detail.amount_haber = null"
                        @blur="normalizeAmount(detail, 'amount_debe')"
                      ></v-text-field>
                      <span v-if="isInvalidAmount(detail.amount_debe)" class="red--text caption">
                        {{ formatUtils.parseAmount(detail.amount_debe) === null ? "Importe invalido." : "Mayor a 0." }}
                      </span>
                    </template>
                  </td>
                  <td style="min-width:120px" :class="isLockedFields() ? 'text-right' : ''">
                    <span v-if="isLockedFields()">{{ parseFloat(detail.amount_haber) > 0 ? formatUtils.formatMoney(detail.amount_haber) : "—" }}</span>
                    <template v-else>
                      <v-text-field
                        v-model="detail.amount_haber"
                        hide-details
                        :disabled="!!detail.amount_debe"
                        class="py-1 input-right"
                        @input="detail.amount_debe = null"
                        @blur="normalizeAmount(detail, 'amount_haber')"
                      ></v-text-field>
                      <span v-if="isInvalidAmount(detail.amount_haber)" class="red--text caption">
                        {{ formatUtils.parseAmount(detail.amount_haber) === null ? "Importe invalido." : "Mayor a 0." }}
                      </span>
                    </template>
                  </td>
                  <td style="min-width:160px">
                    <v-text-field
                      v-model="detail.description"
                      placeholder="Motivo de la línea..."
                      hide-details
                      :disabled="isViewModal()"
                      class="py-1"
                    ></v-text-field>
                  </td>
                  <td v-if="!isLockedFields()" style="width:40px">
                    <v-icon color="red lighten-1" small @click="removeDetail(idx)">delete</v-icon>
                  </td>
                </tr>
                <tr v-if="item.details.length === 0">
                  <td colspan="7" class="text-center grey--text py-4">Sin detalles — hacé click en "Agregar Item"</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Panel de totales -->
          <div class="d-flex justify-end mt-4 mb-2">
            <v-card outlined class="pa-4" style="min-width: 300px">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="body-2 grey--text text--darken-1">Total Debe</span>
                <span class="body-2 font-weight-medium">{{ formatUtils.formatMoney(totalDebe) }}</span>
              </div>
              <div class="d-flex justify-space-between align-center mb-3">
                <span class="body-2 grey--text text--darken-1">Total Haber</span>
                <span class="body-2 font-weight-medium">{{ formatUtils.formatMoney(totalHaber) }}</span>
              </div>
              <v-divider class="mb-3"></v-divider>
              <div
                class="d-flex justify-space-between align-center rounded pa-3"
                :style="isBalanced ? 'background:#E8F5E9' : 'background:#FFF3E0'"
              >
                <div>
                  <div class="caption font-weight-bold text-uppercase grey--text text--darken-2">Total Movimiento</div>
                  <div class="caption font-weight-medium" :class="isBalanced ? 'green--text text--darken-1' : 'orange--text text--darken-3'">
                    {{ isBalanced ? 'BALANCEADO' : 'SIN BALANCEAR' }}
                  </div>
                </div>
                <span class="text-h6 font-weight-bold" :class="isBalanced ? 'green--text text--darken-1' : 'red--text'">
                  {{ formatUtils.formatMoney(totalDebe) }}
                </span>
              </div>
            </v-card>
          </div>

        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="$emit('updateDialog', false)">
            {{ isViewModal() ? 'Cerrar' : 'Cancelar' }}
          </v-btn>
          <v-btn
            v-if="revertOf !== null"
            color="warning"
            text
            @click="$emit('confirmRevert', revertOf)"
            :loading="loading"
          >
            Confirmar Reversión
          </v-btn>
          <v-btn
            v-if="!isViewModal()"
            color="success"
            text
            type="submit"
            :loading="loading"
          >
            Guardar
          </v-btn>
        </v-card-actions>

      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import Constant from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { emmiterService } from "@/libs/ws/emmiterService";
import { tesoreriaMovementTemplateService } from "@/libs/ws/tesoreriaMovementTemplateService";
import { formatUtils } from "@/libs/utils/formatUtils";
import ModalGenericMixin from "@/mixins/ModalGenericMixin.vue";
import ConceptoCentroPicker from "@/components/ConceptoCentroPicker.vue";
import DatePickerEsp from "@/components/DatePickerEsp.vue";

export default {
  mixins: [ModalGenericMixin],
  components: { ConceptoCentroPicker, DatePickerEsp },
  props: {
    modal_title: String,
    dialog: Boolean,
    item: Object,
    formBtn: String,
    service: Object,
    revertOf: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      formatUtils: formatUtils,
      open: this.dialog,
      submitted: false,
      money_accounts: [],
      conceptos: [],
      cost_centers: [],
      clases_comprobante: [],
      emitters: [],
      templates: [],
      selected_template: null,
    };
  },
  computed: {
    formTitle() {
      if (this.item.id === "") return "Nuevo " + this.modal_title;
      return (this.isViewModal() ? "Ver " : "Editar ") + this.modal_title + (this.item.nro_mov ? " #" + this.item.nro_mov : "");
    },
    totalDebe() {
      return (this.item.details || []).reduce((sum, d) => sum + (formatUtils.parseAmount(d.amount_debe) || 0), 0);
    },
    totalHaber() {
      return (this.item.details || []).reduce((sum, d) => sum + (formatUtils.parseAmount(d.amount_haber) || 0), 0);
    },
    isBalanced() {
      return this.totalDebe > 0 && Math.abs(this.totalDebe - this.totalHaber) <= 0.01;
    },
  },
  watch: {
    dialog(val) {
      this.open = val;
      this.errors.clear();
      this.$validator.reset();
      if (val) {
        this.autoSelectEmitter();
        this.loadMoneyAccounts();
        this.selected_template = null;
        this.submitted = false;
      }
    },
    open(val) {
      if (!val) {
        this.$emit("updateDialog", false);
        this.submitted = false;
      }
    },
    "item.emitter"() {
      this.loadMoneyAccounts();
    },
  },
  mounted() {
    this.loadComboData();
  },
  methods: {
    isViewModal() {
      return this.formBtn === "";
    },
    //Un movimiento ya registrado solo admite edicion de textos (ver TesoreriaMovementController::update)
    isEditModal() {
      return this.item.id !== "" && !this.isViewModal();
    },
    //Campos inmutables: bloqueados tanto al ver como al editar
    isLockedFields() {
      return this.isViewModal() || this.isEditModal();
    },
    //Reescribe el campo con el importe ya normalizado (soporta lo pegado desde Excel: "10.822.496,52")
    normalizeAmount(detail, field) {
      detail[field] = formatUtils.normalizeAmountInput(detail[field]);
    },
    isInvalidAmount(value) {
      if (value === null || value === undefined || value === "") return false;
      //Ignora los estados intermedios mientras se tipea ("-", ",", "-,")
      if (/^[-.,\s]*$/.test(String(value))) return false;
      const parsed = formatUtils.parseAmount(value);
      return parsed === null || parsed <= 0;
    },
    addDetail() {
      this.item.details.push({
        money_account: null,
        concepto: null,
        cost_center: null,
        amount_debe: null,
        amount_haber: null,
        description: null,
      });
    },
    removeDetail(idx) {
      this.item.details.splice(idx, 1);
    },
    applyTemplate(tpl) {
      if (!tpl) return;
      this.item.clase_comprobante = tpl.clase_comprobante || null;
      this.item.emitter = tpl.emitter || null;
      this.item.concept = tpl.concept || null;
      this.item.reference = tpl.reference || null;
      this.item.details = (tpl.details || []).map((d) => ({
        money_account: d.money_account || null,
        concepto: d.concepto || null,
        cost_center: d.cost_center || null,
        amount_debe: null,
        amount_haber: null,
        description: d.description || null,
      }));
    },
    loadComboData() {
      this.loadMoneyAccounts();

      generalService.setSection(Constant.SECTION_CONCEPTOS);
      generalService.filter().then((r) => { this.conceptos = r.result; }).catch(() => {});

      generalService.setSection(Constant.SECTION_COSTS_CENTERS);
      generalService.filter().then((r) => {
        this.cost_centers = r.result.map((cc) => ({ id: cc.id, name: cc.name }));
      }).catch(() => {});

      generalService.setSection(Constant.SECTION_TESORERIA_CLASE_COMPROBANTE);
      generalService.filter().then((r) => { this.clases_comprobante = r.result; }).catch(() => {});

      tesoreriaMovementTemplateService.filter().then((r) => { this.templates = r.result; }).catch(() => {});

      emmiterService.filter({ enabled: true }).then((r) => {
        this.emitters = r.result;
        this.autoSelectEmitter();
      }).catch(() => {});
    },
    //Preselecciona el unico emisor disponible. Se llama tambien al abrir el modal porque
    //el reset del item deja emitter en null tras crear un movimiento
    autoSelectEmitter() {
      if (this.emitters.length === 1 && !this.item.emitter) {
        this.item.emitter = this.emitters[0];
      }
    },
    loadMoneyAccounts() {
      const param = {};
      if (this.item.emitter && this.item.emitter.id) {
        param.emitter = { id: this.item.emitter.id };
      }
      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      generalService.filter(param).then((r) => { this.money_accounts = r.result; }).catch(() => {});
    },
    //En edicion solo viajan los textos: el back ignora cualquier otro campo del movimiento
    saveEdit() {
      const payload = {
        id: this.item.id,
        concept: this.item.concept,
        reference: this.item.reference,
        details: this.item.details.map((d) => ({ id: d.id, description: d.description })),
      };
      this.loading = true;
      this.sendRequest(this.service.update(payload), true);
    },
    sendRequest(request, isUpdate) {
      request
        .then((r) => {
          if (r.status) {
            this.$toast.success(
              "El " + this.modal_title + (isUpdate ? " se modifico correctamente." : " se creo correctamente."),
              "OK"
            );
            this.$emit("updateDialog", false);
            this.$emit("loadList");
          } else {
            this.$toast.error(r.message || r.msg, "Error");
          }
        })
        .catch((e) => { this.$toast.error((e && (e.message || e.msg)) || "Error al guardar.", "Error"); })
        .finally(() => { this.loading = false; });
    },
    createItem() {
      this.$validator.validate().then((valid) => {
        if (!valid) return;

        if (this.isEditModal()) {
          this.saveEdit();
          return;
        }

        this.submitted = true;

        if (!this.item.date) {
          this.$toast.error("Debe ingresar la fecha.", "Error");
          return;
        }

        if (this.item.details.length === 0) {
          this.$toast.error("Debe agregar al menos un detalle.", "Error");
          return;
        }

        for (let i = 0; i < this.item.details.length; i++) {
          const d = this.item.details[i];
          if (!d.money_account || !d.money_account.id) {
            this.$toast.error(`Línea ${i + 1}: debe seleccionar una Cuenta.`, "Error");
            return;
          }
          const debe = formatUtils.parseAmount(d.amount_debe) || 0;
          const haber = formatUtils.parseAmount(d.amount_haber) || 0;
          if (this.isInvalidAmount(d.amount_debe)) {
            this.$toast.error(`Línea ${i + 1}: el valor de Debe debe ser un número mayor a 0.`, "Error");
            return;
          }
          if (this.isInvalidAmount(d.amount_haber)) {
            this.$toast.error(`Línea ${i + 1}: el valor de Haber debe ser un número mayor a 0.`, "Error");
            return;
          }
          if (debe <= 0 && haber <= 0) {
            this.$toast.error(`Línea ${i + 1}: debe ingresar un valor de Debe o Haber.`, "Error");
            return;
          }
        }

        const totalDebe = parseFloat(this.totalDebe || 0);
        const totalHaber = parseFloat(this.totalHaber || 0);

        if (totalDebe <= 0) {
          this.$toast.error("El importe del movimiento debe ser mayor a 0.", "Error");
          return;
        }

        if (Math.abs(totalDebe - totalHaber) > 0.01) {
          this.$toast.error("El movimiento debe estar balanceado: Debe y Haber deben ser iguales.", "Error");
          return;
        }

        const payload = {
          ...this.item,
          date: this.item.date,
          amount: parseFloat(totalDebe.toFixed(2)),
          concept: this.item.concept,
          reference: this.item.reference,
          clase_comprobante: this.item.clase_comprobante ? { id: this.item.clase_comprobante.id } : null,
          emitter: this.item.emitter ? { id: this.item.emitter.id } : null,
          details: this.item.details.map((d) => ({
            movement_type: formatUtils.parseAmount(d.amount_debe) ? "D" : "H",
            amount: formatUtils.parseAmount(d.amount_debe) || formatUtils.parseAmount(d.amount_haber) || 0,
            description: d.description,
            money_account: d.money_account ? { id: d.money_account.id } : null,
            concepto: d.concepto ? { id: d.concepto.id } : null,
            cost_center: d.cost_center ? { id: d.cost_center.id } : null,
          })),
        };

        this.loading = true;
        this.sendRequest(this.service.create(payload), false);
      });
    },
  },
};
</script>

<style scoped>
.template-card {
  background-color: #E3F2FD !important;
  border-color: #90CAF9 !important;
}

.detail-th {
  background-color: #F5F5F5 !important;
  color: #616161 !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.input-right input {
  text-align: right;
}
</style>
